import { useEffect, useMemo, useRef, useState, type RefObject } from 'react'
import { useQuery } from '@tanstack/react-query'
import { doc, getDoc } from 'firebase/firestore'
import { useAuth } from '../../auth/AuthContext'
import { firestore } from '../../lib/db'
import { apiGet } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import { subscribeChatReads, subscribeChats } from '../../lib/rt'
import type { Chat as ChatDoc, ChatReads, Project } from '../../lib/types'
import { Avatar, EmptyState, timeAgo } from '../common'
import { Conversation } from './Conversation'
import { NewChatModal } from './NewChatModal'
import { useNombres } from '../autoria'
import { chatSinLeer } from '../../lib/autoria'
import {
  chatTitle,
  dmOtherOf,
  IconoChatProyecto,
  nameOf,
  useTeamRoster,
  useToast,
} from './helpers'
import { useChatBubble } from './bubble'
import { useCerrarAlClicFuera } from '../../lib/useCerrarAlClicFuera'

// Cuerpo del panel de la burbuja. SOLO se monta con el panel abierto, así que aquí (y
// solo aquí) viven los listeners de bandeja: al cerrar la burbuja se desmontan y el
// presupuesto vuelve a cero (realtime-y-reglas.md). Reusa la misma bandeja, el mismo
// NewChatModal y el mismo componente Conversation que la página /chat.

export function BubblePanel({
  hexRef,
  saliendo = false,
}: {
  // El hexágono que abre/cierra: se excluye del "clic fuera" para que su propio
  // toggle no pelee con el listener (§3.8).
  hexRef?: RefObject<HTMLButtonElement | null>
  saliendo?: boolean
}) {
  const bubble = useChatBubble()
  const panelRef = useRef<HTMLDivElement>(null)
  const { user } = useAuth()
  const meEmail = user?.email ?? ''
  const meName = user?.name ?? meEmail

  const [chats, setChats] = useState<ChatDoc[] | null>(null)
  const [reads, setReads] = useState<ChatReads | null>(null)
  const [fallbackChat, setFallbackChat] = useState<ChatDoc | null>(null)
  const [menu, setMenu] = useState(false)
  const [modal, setModal] = useState<'dm' | 'group' | null>(null)
  const [q, setQ] = useState('')
  const [toastNode, toast] = useToast()

  // Punto 4 de Damián: el panel se cierra al hacer clic FUERA y con Escape, sin
  // robarle el clic a lo que haya debajo. El hook vive aquí (y no en el provider)
  // porque este componente solo existe con el panel abierto: cero listeners en reposo.
  useCerrarAlClicFuera(panelRef, bubble.close, hexRef ? [hexRef] : [])

  // Bandeja + estado de lectura: listeners atados al ciclo de vida del panel.
  useEffect(() => {
    if (!meEmail) return
    const unChats = subscribeChats(meEmail, setChats)
    const unReads = subscribeChatReads(meEmail, setReads)
    return () => {
      unChats()
      unReads()
    }
  }, [meEmail])

  const team = useTeamRoster()
  // Roster + directorio histórico (D-25): el Campfire importado nombra a quien
  // escribió aunque todavía no tenga cuenta. El roster gana.
  const names = useNombres()

  // Nombres de proyectos para los canales 🔗 (cache compartido con /chat y dashboard).
  const projQ = useQuery({
    queryKey: qk.projects('mine'),
    queryFn: () => apiGet<{ projects: Project[] }>('/api/projects?filter=mine').then((r) => r.projects),
  })
  const projectNames = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of projQ.data ?? []) m.set(p.id, p.name)
    return m
  }, [projQ.data])

  // ---- resolución del chat abierto (bandeja → semilla → getDoc puntual) ----
  const openChatId = bubble.view.kind === 'conversation' ? bubble.view.chatId : null
  const loaded = chats !== null
  const inList = !!openChatId && !!chats?.some((c) => c.id === openChatId)
  const seedMatches = bubble.seedChat && bubble.seedChat.id === openChatId ? bubble.seedChat : null

  useEffect(() => {
    setFallbackChat(null)
    if (!openChatId || !loaded || inList || seedMatches) return
    let alive = true
    void getDoc(doc(firestore, 'chats', openChatId))
      .then((s) => {
        if (alive && s.exists()) setFallbackChat(s.data() as ChatDoc)
      })
      .catch(() => {
        /* permission-denied: lo maneja el listener de mensajes (G-7) */
      })
    return () => {
      alive = false
    }
  }, [openChatId, loaded, inList, seedMatches])

  const openChat = openChatId
    ? (chats?.find((c) => c.id === openChatId) ?? seedMatches ?? fallbackChat)
    : null

  // ---- bandeja filtrada por la barra de búsqueda (en memoria) ----
  const term = q.trim().toLowerCase()
  const filteredChats = useMemo(() => {
    if (!chats) return null
    if (!term) return chats
    return chats.filter((c) =>
      chatTitle(c, meEmail, names, projectNames).toLowerCase().includes(term),
    )
  }, [chats, term, meEmail, names, projectNames])

  // Si la búsqueda no encuentra ningún chat, ofrecemos el roster para abrir un DM directo.
  const rosterMatches = useMemo(() => {
    if (!term || (filteredChats && filteredChats.length > 0)) return []
    return team
      .filter((m) => m.isActive !== false && m.email !== meEmail)
      .filter(
        (m) => m.name.toLowerCase().includes(term) || m.email.toLowerCase().includes(term),
      )
      .slice(0, 6)
  }, [term, filteredChats, team, meEmail])

  const isConversation = bubble.view.kind === 'conversation'

  return (
    <div
      ref={panelRef}
      className={`chat-bubble-panel card${isConversation ? ' is-conv' : ''}${saliendo ? ' saliendo' : ''}`}
      role="dialog"
      aria-label="chats"
    >
      {isConversation && openChatId ? (
        <Conversation
          key={openChatId}
          chatId={openChatId}
          chat={openChat}
          title={openChat ? chatTitle(openChat, meEmail, names, projectNames) : '…'}
          meEmail={meEmail}
          meName={meName}
          names={names}
          team={team}
          muted={reads?.chats?.[openChatId]?.muted ?? false}
          onDenied={() => {
            toast('Ya no eres miembro de este chat')
            bubble.showList()
          }}
          onBack={bubble.showList}
          onClose={bubble.close}
          onLeft={bubble.showList}
          toast={toast}
        />
      ) : (
        <>
          <header className="chat-bp-head">
            <strong className="chat-bp-title">chats</strong>
            <div className="chat-bp-plus">
              <button
                type="button"
                className="icon-btn"
                onClick={() => setMenu((m) => !m)}
                aria-label="Nuevo chat o grupo"
                aria-expanded={menu}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </button>
              {menu && (
                <>
                  <div className="chat-bp-menu-scrim" role="presentation" onClick={() => setMenu(false)} />
                  <div className="chat-bp-menu pop" role="menu" data-capa-flotante>
                    <button
                      type="button"
                      className="pop-item"
                      role="menuitem"
                      onClick={() => {
                        setMenu(false)
                        setModal('dm')
                      }}
                    >
                      Nuevo mensaje
                    </button>
                    <button
                      type="button"
                      className="pop-item"
                      role="menuitem"
                      onClick={() => {
                        setMenu(false)
                        setModal('group')
                      }}
                    >
                      Nuevo grupo
                    </button>
                  </div>
                </>
              )}
            </div>
            <button type="button" className="icon-btn" onClick={bubble.close} aria-label="Cerrar chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </header>

          <div className="chat-bp-body">
            {bubble.error && (
              <div className="chat-bp-error" role="alert">
                <span>{bubble.error}</span>
                <button type="button" className="btn btn-ghost" onClick={bubble.clearError}>
                  Entendido
                </button>
              </div>
            )}

            {bubble.dmPending || chats === null ? (
              <div className="chat-inbox-skel">
                <div className="skel" style={{ height: 52 }} />
                <div className="skel" style={{ height: 52 }} />
                <div className="skel" style={{ height: 52 }} />
              </div>
            ) : chats.length === 0 ? (
              <EmptyState
                title="Sin conversaciones"
                hint="Empieza un mensaje directo o crea un grupo con «+»."
              />
            ) : (
              <>
                <ul className="chat-list">
                  {(filteredChats ?? []).map((chat) => {
                    const r = reads?.chats?.[chat.id]
                    // Misma regla que la bandeja (lib/autoria.ts): max(readAt, leidoHasta).
                    const unread = chatSinLeer(chat, r?.readAt, meEmail)
                    const title = chatTitle(chat, meEmail, names, projectNames)
                    return (
                      <li key={chat.id}>
                        <button
                          type="button"
                          className={`chat-item${unread ? ' unread' : ''}`}
                          onClick={() => bubble.openChatDoc(chat)}
                        >
                          {chat.type === 'project' ? (
                            <IconoChatProyecto />
                          ) : (
                            <Avatar
                              who={chat.type === 'group' ? (chat.name ?? 'Grupo') : nameOf(names, dmOtherOf(chat, meEmail))}
                            />
                          )}
                          <span className="chat-item-main">
                            <span className="chat-item-top">
                              <span className="chat-item-name">{title}</span>
                              {chat.lastMessage.text && (
                                <span className="chat-item-when">{timeAgo(chat.lastMessage.at)}</span>
                              )}
                            </span>
                            <span className="chat-item-snippet">
                              {chat.lastMessage.text
                                ? `${chat.lastMessage.by === meEmail ? 'Tú' : nameOf(names, chat.lastMessage.by)}: ${chat.lastMessage.text}`
                                : 'Sin mensajes aún'}
                            </span>
                          </span>
                          {unread && <span className="chat-dot" aria-label="No leído" />}
                        </button>
                      </li>
                    )
                  })}
                </ul>

                {/* Sin match en la bandeja: ofrecemos abrir un DM directo desde el roster. */}
                {term && filteredChats && filteredChats.length === 0 && (
                  rosterMatches.length > 0 ? (
                    <div className="chat-bp-roster">
                      <p className="chat-bp-roster-label">Abrir conversación con…</p>
                      <ul className="chat-list">
                        {rosterMatches.map((m) => (
                          <li key={m.email}>
                            <button
                              type="button"
                              className="chat-item"
                              onClick={() => {
                                setQ('')
                                bubble.openDm(m.email)
                              }}
                            >
                              <Avatar who={m.name} />
                              <span className="chat-item-main">
                                <span className="chat-item-name">{m.name}</span>
                                <span className="chat-item-snippet">{m.email}</span>
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p className="state chat-bp-noresults">Nadie coincide con «{q.trim()}».</p>
                  )
                )}
              </>
            )}
          </div>

          <div className="chat-bp-search">
            <label className="search">
              <span aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
              </span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar chat o persona…"
                aria-label="Buscar chat o persona"
              />
            </label>
          </div>
        </>
      )}

      {modal && (
        <NewChatModal
          mode={modal}
          meEmail={meEmail}
          meName={meName}
          team={team}
          onClose={() => setModal(null)}
          onCreated={(chat) => {
            setModal(null)
            bubble.openChatDoc(chat)
          }}
          toast={toast}
        />
      )}
      {toastNode}
    </div>
  )
}
